import axios from 'axios';
import { isLoggedStore } from '../store/logged';
import { ElMessage } from 'element-plus';


// URL del backend: sovrascrivibile in locale con VUE_APP_API_URL in un file
// .env.local (non versionato), es. per puntare a un ambiente di staging.
export const BASE_URL = process.env.VUE_APP_API_URL || 'https://gestionalesimba.it/';

const API = axios.create({
  baseURL: BASE_URL,
  headers: { 
    'Access-Control-Allow-Origin': '*',  
  }
});

// ---------------------------------------------------------------------------
//  TOKEN E RINNOVO
//
//  ⚠️ I token si leggono dallo STORE, non da `localStorage`: lo store li
//  tiene con `useStorage`, che scrive su localStorage IN DIFFERITA. Leggendo
//  localStorage subito dopo un rinnovo si otteneva ancora il token VECCHIO,
//  la chiamata partiva scaduta e il 401 chiudeva la sessione. Era la causa
//  dei «Sessione scaduta» dopo pochi minuti.
//
//  ⚠️ Un solo rinnovo in volo: il server RUOTA il refresh token a ogni
//  rinnovo e tiene buono solo l'ultimo. Con N chiamate in parallelo partivano
//  N rinnovi, ognuno invalidava il precedente e chi arrivava dopo riceveva
//  «Access Denied» — di nuovo logout.
// ---------------------------------------------------------------------------

/** Decodifica il payload di un JWT (senza verificarlo: quello tocca al server). */
const parseJwt = (token) => {
  if (!token) return null;
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch {
    return null;
  }
};

const getAccessToken = () => isLoggedStore().accessToken || '';
const getRefreshToken = () => isLoggedStore().refreshToken || '';

/** Scaduto, con un margine: un token che sta per scadere non va usato, perché
 *  scadrebbe mentre la richiesta è in viaggio. */
const isExpired = (expMs, skewMs = 5000) => {
  const exp = Number(expMs);
  return !exp || Date.now() >= exp - skewMs;
};
const isAccessTokenExpired = () => isExpired(isLoggedStore().jwtExp);
const isRefreshTokenExpired = () => isExpired(isLoggedStore().refreshExp);

/** Salva la coppia di token e le relative scadenze (ms). */
const applyTokens = ({ access_token: accessToken, refreshtoken }) => {
  const store = isLoggedStore();
  store.accessToken = accessToken;
  store.refreshToken = refreshtoken;
  store.jwtExp = (parseJwt(accessToken)?.exp ?? 0) * 1000;
  store.refreshExp = (parseJwt(refreshtoken)?.exp ?? 0) * 1000;
};

let refreshInFlight = null;

/** Rinnova i token. Istanza axios "nuda", per non rientrare negli interceptor.
 *
 *  ⚠️ L'indirizzo si compone con `baseURL`, NON incollando le stringhe. Con un
 *  `VUE_APP_API_URL` senza barra finale, `${BASE_URL}auth/refresh` diventava
 *  `https://api.esempio.itauth/refresh`: un dominio che non esiste, quindi
 *  errore di rete a ogni rinnovo e sessione chiusa dopo dieci minuti. Tutto il
 *  resto continuava a funzionare, perché passa da `axios.create({ baseURL })`
 *  che unisce i pezzi per bene — ed è esattamente ciò che fa `baseURL` qui.
 */
const refreshTokens = () => {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .get('/auth/refresh', {
        baseURL: BASE_URL,
        headers: { Authorization: `Bearer ${getRefreshToken()}` },
      })
      .then((res) => applyTokens(res.data))
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
};

const isAuthUrl = (url = '') => /\/auth\/(login|refresh|reset-password)/.test(url);

// Interceptor di REQUEST: token sempre fresco prima di partire.
API.interceptors.request.use(
  async (config) => {
    if (isAuthUrl(config.url)) return config;

    if (getAccessToken() && isAccessTokenExpired() && !isRefreshTokenExpired()) {
      try {
        await refreshTokens();
      } catch (e) {
        // Si lascia partire comunque: al 401 ci pensa l'interceptor di
        // risposta, che riprova una volta e poi chiude la sessione.
        console.warn('Rinnovo della sessione non riuscito:', e?.response?.status ?? e?.message);
      }
    }

    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Interceptor di RESPONSE: gestione centralizzata degli errori HTTP.
// Il backend (F0–F7) risponde con codici semantici:
//   401 token assente/scaduto        -> sessione chiusa, redirect al login
//   403 non autorizzato/capability   -> messaggio, nessun redirect
//   404 risorsa inesistente o di altro tenant
//   400 regola di dominio violata (es. "1 account = 1 partner")
//   410 servizio dismesso (Escort/Delivery)
// Le pagine che gestiscono già in proprio un errore possono disattivare il
// messaggio globale passando { skipErrorToast: true } nella config axios.
// ---------------------------------------------------------------------------

const clearSessionAndGoToLogin = async () => {
  const store = isLoggedStore();
  store.isLogged = false;
  store.viewMenu = false;
  store.accessToken = '';
  store.refreshToken = '';
  store.refreshExp = '';
  store.jwtExp = '';
  store.id = '';
  store.mail = '';
  store.role = '';
  store.employee = {};
  // import dinamico per evitare il ciclo API.js -> router -> views -> API.js
  const { default: router } = await import('../router');
  if (router.currentRoute.value.name !== 'Login') {
    router.push({ name: 'Login' });
  }
};

// Estrae un messaggio leggibile dal body d'errore NestJS ({ message: string|string[] })
const backendMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return '';
  if (Array.isArray(data.message)) return data.message.join(', ');
  if (typeof data.message === 'string') return data.message;
  if (typeof data === 'string') return data;
  return '';
};

const notify = (message, type = 'error') => {
  ElMessage({ message, type, grouping: true, duration: 5000, showClose: true });
};

API.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const config = error?.config || {};
    const url = config.url || '';
    const detail = backendMessage(error);

    if (!error.response) {
      // Nessuna risposta: rete giù, CORS, certificato non accettato...
      if (!config.skipErrorToast) {
        notify('Impossibile contattare il server. Verifica la connessione.');
      }
      return Promise.reject(error);
    }

    // Login: la pagina gestisce da sé username/password errati
    if (url.includes('/auth/login')) {
      return Promise.reject(error);
    }

    switch (status) {
      case 401:
        // Token rifiutato (revocato, ruotato altrove, orologi sfasati): si
        // prova UNA volta a rinnovare e si rifà la chiamata. La sessione si
        // chiude solo se anche il rinnovo fallisce.
        if (
          config && !config._retried && !isAuthUrl(url)
          && getRefreshToken() && !isRefreshTokenExpired()
        ) {
          config._retried = true;
          return refreshTokens()
            .then(() => {
              config.headers.Authorization = `Bearer ${getAccessToken()}`;
              return API.request(config);
            })
            .catch(() => {
              if (isLoggedStore().isLogged) {
                notify('Sessione scaduta: effettua di nuovo il login.', 'warning');
                clearSessionAndGoToLogin();
              }
              return Promise.reject(error);
            });
        }
        // Solo se c'era una sessione: da sloggati (pagina di login) una
        // chiamata senza token non è una "sessione scaduta".
        if (isLoggedStore().isLogged) {
          notify('Sessione scaduta: effettua di nuovo il login.', 'warning');
          clearSessionAndGoToLogin();
        }
        break;
      case 403:
        if (!config.skipErrorToast) {
          notify(
            'Operazione non autorizzata' + (detail ? `: ${detail}` : '.')
          );
        }
        break;
      case 404:
        if (!config.skipErrorToast) {
          notify('Risorsa non trovata o non accessibile.', 'warning');
        }
        break;
      case 400:
        if (!config.skipErrorToast) {
          notify(
            'Richiesta non valida' + (detail ? `: ${detail}` : '.'),
            'warning'
          );
        }
        break;
      case 410:
        if (!config.skipErrorToast) {
          notify('Servizio dismesso: questa funzione non è più disponibile.');
        }
        break;
      default:
        if (status >= 500 && !config.skipErrorToast) {
          notify('Errore del server, riprova più tardi.');
        }
    }
    return Promise.reject(error);
  }
);

export default API;